import {
  Task,
  Step,
  Comment,
  LogTimer,
  Trigger,
  TaskForm,
  Template,
  Snippet
} from "./entities";

export class CommandCreateComment {
  constructor(comment, client) {
    let user = comment.relationships.users.data.id
      ? {
          id: comment.relationships.users.data.id,
          title: `${comment.relationships.users.data.attributes.first_name} ${comment.relationships.users.data.attributes.last_name}`,
          alt: `${comment.relationships.users.data.attributes.first_name} ${comment.relationships.users.data.attributes.last_name}`,
          subTitle: comment.relationships.users.data.attributes.email,
          background: comment.relationships.users.data.attributes.color,
          is_client: comment.relationships.users.data.attributes.is_client,
        }
      : client.length
      ? { ...client[0], id: null }
      : {};

    this.data = new Comment({
      created_at: comment.attributes.created_at,
      updated_at: comment.attributes.updated_at,
      internal: !!comment.attributes.internal,
      // content_html has S3 <img> sources rewritten to signed/proxied URLs by
      // the backend; fall back to raw content when absent (older API responses).
      // This is the value the editor is re-seeded with when a comment is edited,
      // so it must keep the author's {{field}} tokens unresolved.
      content: comment.attributes.content_html || comment.attributes.content,
      // Same body with the dropdown-inserted fields resolved -- display only.
      content_display: comment.attributes.content_display,
      reply: comment.attributes.reply,
      reply_comments: comment.attributes.reply_comments,
      task_id: comment.attributes.task_id,
      user,
      id: comment.id,
    });
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateTask {
  constructor(task, { requestSettings, generalSettings }) {
    this.data = new Task({
      ticket_: task.attributes.ticket,
      added: task.attributes.added,
      created_at: task.attributes.created_at,
      updated_at: task.attributes.updated_at,
      is_request: task.attributes.is_request,
      master_id: task.attributes.master_id,
      is_form: task.attributes.is_form,
      started_at: task.attributes.started_at,
      project_name: task.attributes.project_name,
      approval_stage: task.attributes.approval_stage,
      projectId: task.relationships.steps.data.attributes.project_id,
      archivers: task.attributes.archivers,
      due_date: task.attributes.closed_at,
      put_to_step_at: task.attributes.put_to_step_at,
      put_to_step: task.attributes.put_to_step,
      request_data: task.attributes.request_data,
      has_notification: task.attributes.has_notification,
      has_client_reply: task.attributes.has_client_reply,
      latest_feedback: task.attributes.latest_feedback,
      requestSettings: requestSettings,
      ticket_number: task.attributes.ticket_number,
      generalSettings: generalSettings,
      is_completed: task.attributes.is_completed,
      is_closed: task.attributes.is_closed,
      title: task.attributes.title,
      cron_expression:
        task.relationships.schedules.data[0]?.attributes.frequency,

      task_system_id: task.attributes.task_system_id,
      files_number: task.attributes.files_number,
      incomplete_subtasks_number: task.attributes.incomplete_subtasks_number,
      description: task.attributes.description,
      step_id: task.attributes.step_id,
      comments_number: task.attributes.comments_number,
      notes_number: task.attributes.notes_number || 0,
      subtasks_number: task.attributes.subtasks_number,
      owner: task.attributes.task_user_id,
      spentTime: task.attributes.spent_time,
      is_follow: task.attributes.is_follow,
      task_form_data: task.attributes.task_form_data,
      id: task.id,
      master:
        task.relationships.master.length === undefined
          ? task.relationships.master.data[0]
          : null,

      assignees: task.relationships.assignees.data.map(({ attributes, id }) => {
        return {
          id,
          title: `${attributes.first_name} ${attributes.last_name}`,
          alt: `${attributes.first_name} ${attributes.last_name}`,
          subTitle: attributes.email,
          background: attributes.color,
        };
      }),

      slaves: task.relationships.slaves.data.map(({ attributes, id }) => {
        return {
          id,
          ...attributes,
        };
      }),

      client_user: task.attributes.client_user
        ? {
            ...task.attributes.client_user,
            title: `${task.attributes.client_user.first_name} ${task.attributes.client_user.last_name}`,
          }
        : null,

      clients: task.relationships.clients.data.map(({ attributes, id }) => {
        return {
          id,
          userID: attributes.userID,
          locationId: attributes.locationId,
          title: `${attributes.firstName} ${attributes.lastName}`,
          alt: `${attributes.firstName} ${attributes.lastName}`,
          subTitle: attributes.email,
          phone: attributes.phone,
          background: attributes.color,
          account: attributes.account,
          sub_account_name: attributes.sub_account_name,
        };
      }),
      index: 0,
      tags: task.relationships.tags.data.map((p) => ({
        id: p.id,
        label: p.attributes.label,
        variant: p.attributes.variant,
      })),
      priority: task.attributes.priority,
      estimated_time: task.attributes.estimated_time,
      column: task.column || task.relationships.steps.data.attributes,
    });
    this.data.resolveAll();
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateStep {
  constructor(step) {
    this.data = new Step({
      id: step.id,
      index: step.index,
      created_at: step.attributes.created_at,
      updated_at: step.attributes.updated_at,
      position: step.attributes.position,
      // project_id: rest.project_id,
      name: step.attributes.title,
      color: step.attributes.color,
      encryptId: step.attributes.encryptId || null,
      tasks: [],
    });
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateLogTimer {
  constructor(timer, project) {
    this.data = new LogTimer({
      id: timer.id,
      index: timer.index,
      createdAt: timer.attributes.created_at,
      updatedAt: timer.attributes.updated_at,
      date: timer.attributes.date,
      description: timer.attributes.description,
      user: timer.relationships.users.data,
      timeSpend: timer.attributes.time_spend,
      startTime: timer.attributes.start_time,
      isClose: timer.attributes.has_been_close,
      top: timer.attributes.position_top,
      left: timer.attributes.position_left,
      taskID: timer.attributes.task_id,
      stepID: timer.attributes.step_id,
      isRunning: timer.attributes.is_running,
      project: project,
      task: timer.attributes.task,
      tags: timer.relationships.tags.data.map((tag) => ({
        variant: tag.attributes.variant,
        label: tag.attributes.label,
        id: tag.id,
      })),
    });
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateTrigger {
  constructor(trigger) {
    this.data = new Trigger({
      created_at: trigger.attributes.created_at,
      updated_at: trigger.attributes.updated_at,
      status: trigger.attributes.status,
      title: trigger.attributes.name,
      custom_field_id: trigger.attributes.custom_field_id,
      triggerID: trigger.attributes.trigger_id,
      triggerName: trigger.relationships.triggers.data.attributes.name,
      id: trigger.id,
      tags: trigger.attributes.tags,
      task_tag_ids: trigger.attributes.task_tag_ids,
      move_to_step_id: trigger.attributes.move_to_step_id,
      set_completed: trigger.attributes.set_completed,
      reply_snippet_id: trigger.attributes.reply_snippet_id,
      contacts: trigger.attributes.contacts,
      options: trigger.relationships.trigger_options.data,
      default: trigger.attributes.default,
    });
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateTaskFrom {
  constructor(trigger) {
    this.data = new TaskForm(trigger);
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateTemplate {
  constructor(template) {
    this.data = new Template(template);
  }

  execute() {
    return this.data;
  }
}

export class CommandCreateSnippet {
  constructor(template) {
    this.data = new Snippet(template);
  }

  execute() {
    return this.data;
  }
}
